import React, { FC, memo, useEffect, useState } from 'react';
import MenuManager from '~Scenes/SelfServiceKiosks/Components/MenuManager';
import { useExpressCartSummary } from '../../Providers/ExpressCartProvider';

/** Fire just after the whole minute, so 14:01:00 already counts as 14:01. */
const MINUTE_MS = 60000;
const PAST_MINUTE_MARGIN_MS = 50;

/** Milliseconds until just after the next whole minute on the clock. */
const msUntilNextMinute = () => MINUTE_MS - (Date.now() % MINUTE_MS) + PAST_MINUTE_MARGIN_MS;

interface Props {
  /** True while a shopper is ordering - the menu is never swapped under them. */
  sessionActive: boolean;
}

/**
 * The only MenuManager in EPOS: keeps state.POSMenu on the menu for the cart's
 * service type and the current time. EPOS keeps its service type between
 * shoppers, so a time-controlled menu would never switch on its own - while
 * welcome shows, this re-checks at every whole minute (14:01, 14:02, ...) and
 * once when a session ends. MenuManager has no recheck input of its own, so a
 * tick remounts it via `key`; its query answers from the Apollo cache, and a
 * tick that finds the same menu dispatches the same reference, so nothing
 * downstream re-renders.
 */
const MenuManagerBridge: FC<Props> = ({ sessionActive }) => {
  const expressCart = useExpressCartSummary();
  const [recheckKey, setRecheckKey] = useState(0);

  useEffect(() => {
    if (sessionActive) {
      return undefined;
    }

    /** Back on welcome - the order may have run past a menu switch. */
    setRecheckKey((key) => key + 1);

    let timeout: ReturnType<typeof setTimeout>;

    /** Rescheduled from the clock each time, so it never drifts off the whole minute. */
    const scheduleNextTick = () => {
      timeout = setTimeout(() => {
        setRecheckKey((key) => key + 1);
        scheduleNextTick();
      }, msUntilNextMinute());
    };

    scheduleNextTick();

    return () => clearTimeout(timeout);
  }, [sessionActive]);

  return <MenuManager key={recheckKey} serviceType={expressCart.serviceType} />;
};

export default memo(MenuManagerBridge);
